import { Link } from '@tanstack/react-router'
import type { InvoiceDetailResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@trellis/roots/dropdown-menu'
import { StatusBadge } from '@trellis/roots/status'
import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import {
  Archive,
  ArchiveRestore,
  Ban,
  Check,
  Copy,
  Eye,
  Flag,
  Link2,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Send,
  Unlink,
  XCircle,
} from 'lucide-react'

import { PdfActionMenuItem } from '@/components'
import type { PdfActionState } from '@/components/pdf/PdfActionButton/usePdfAction'
import type { OrganizationReadiness } from '@/hooks'
import { formatCalendarDate } from '@/lib/format'
import { cn } from '@/lib/utils'

const PDF_ALLOWED_STATUSES = new Set(['DRAFT', 'SENT', 'PAID', 'OVERDUE'])

const PARTIALLY_PAID_STATUS_STYLES: Record<string, { pill: string; dot: string }> = {
  PARTIALLY_PAID: { pill: 'bg-amber-50 text-amber-800', dot: 'bg-amber-500' },
}

const PARTIALLY_PAID_STATUS_LABELS: Record<string, string> = {
  PARTIALLY_PAID: 'Partially paid',
}

type StatusTransition = 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED'

export interface InvoiceDetailHeroProps {
  id: string
  invoice: InvoiceDetailResponseDto
  pdfAction: PdfActionState
  invoiceReadiness: OrganizationReadiness
  hasExistingPdf: boolean
  unlinkOriginal: { id: string; label: string } | null
  transitionIsPending: boolean
  archiveIsPending: boolean
  unarchiveIsPending: boolean
  hasPartialPayment: boolean
  onOpenPreview: () => void
  onOpenReissue: () => void
  onOpenLinkReissued: () => void
  onOpenUnlink: () => void
  onOpenDelete: () => void
  onOpenDuplicate: () => void
  onOpenMarkSent: () => void
  onOpenMarkPaid: () => void
  onOpenRegenerate: () => void
  onOpenSend: () => void
  onTransition: (status: StatusTransition) => void
  onArchive: () => void
  onUnarchive: () => void
}

export function InvoiceDetailHero({
  id,
  invoice,
  pdfAction,
  invoiceReadiness,
  hasExistingPdf,
  unlinkOriginal,
  transitionIsPending,
  archiveIsPending,
  unarchiveIsPending,
  hasPartialPayment,
  onOpenPreview,
  onOpenReissue,
  onOpenLinkReissued,
  onOpenUnlink,
  onOpenDelete,
  onOpenDuplicate,
  onOpenMarkSent,
  onOpenMarkPaid,
  onOpenRegenerate,
  onOpenSend,
  onTransition,
  onArchive,
  onUnarchive,
}: InvoiceDetailHeroProps) {
  const isDraft = invoice.status === 'DRAFT'
  const isSent = invoice.status === 'SENT'
  const isOverdue = invoice.status === 'OVERDUE'
  const canGeneratePdf = PDF_ALLOWED_STATUSES.has(invoice.status)
  const canCancel = isDraft || isSent || isOverdue
  const canReissue =
    (invoice.status === 'OVERDUE' || invoice.status === 'CANCELLED') &&
    !invoice.reissuedAs &&
    !invoice.hasSucceededPayment
  const canLinkReissued = invoice.status === 'CANCELLED' && !invoice.reissuedAs
  const isArchived = !!invoice.archivedAt
  const isArchivable = ['PAID', 'CANCELLED', 'VOID'].includes(invoice.status)

  return (
    <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0">
        {invoice.recurringInvoiceId && (
          <Link
            to="/invoices/recurring-invoices/$id"
            params={{ id: invoice.recurringInvoiceId }}
            className="bg-muted text-muted-foreground mb-1.5 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium"
          >
            ↻ RINV
          </Link>
        )}
        <Typography variant="overline" color="muted" className="block">
          {invoice.clientName
            ? `Invoice for ${invoice.clientName}`
            : `Invoice ${invoice.invoiceNumber}`}
        </Typography>
        <Typography
          variant="display-lg"
          as="h1"
          className="mt-1.5 leading-none font-black tracking-tight"
        >
          {invoice.invoiceNumber}
        </Typography>
        <div className="text-muted-foreground mt-2.5 flex flex-wrap items-center gap-3 text-[13.5px]">
          <StatusBadge status={invoice.status} />
          {hasPartialPayment && (
            <StatusBadge
              status="PARTIALLY_PAID"
              styles={PARTIALLY_PAID_STATUS_STYLES}
              labels={PARTIALLY_PAID_STATUS_LABELS}
            />
          )}
          <span>Issued {formatCalendarDate(invoice.issueDate)}</span>
          <span>·</span>
          <span
            className={cn(
              isOverdue ? 'font-medium text-destructive-text' : 'text-muted-foreground',
            )}
          >
            Due {invoice.dueAt ? formatCalendarDate(invoice.dueAt) : '—'}
          </span>
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5 max-md:w-full">
        <div className="flex items-center gap-2 max-md:w-full max-md:flex-row-reverse">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More options">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(isDraft || canGeneratePdf) && (
                <>
                  {isDraft && (
                    <DropdownMenuItem asChild>
                      <Link to="/invoices/$id/edit" params={{ id }}>
                        <Pencil className="mr-2 size-4" />
                        Edit
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canGeneratePdf && <PdfActionMenuItem action={pdfAction} />}
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={onOpenDuplicate}>
                <Copy className="mr-2 size-4" />
                Duplicate
              </DropdownMenuItem>
              {canReissue && (
                <DropdownMenuItem onClick={onOpenReissue}>
                  <RefreshCw className="mr-2 size-4" />
                  Re-issue
                </DropdownMenuItem>
              )}
              {canLinkReissued && (
                <DropdownMenuItem onClick={onOpenLinkReissued}>
                  <Link2 className="mr-2 size-4" />
                  Link re-issued invoice
                </DropdownMenuItem>
              )}
              {unlinkOriginal && (
                <DropdownMenuItem onClick={onOpenUnlink}>
                  <Unlink className="mr-2 size-4" />
                  {unlinkOriginal.label}
                </DropdownMenuItem>
              )}
              {canGeneratePdf && hasExistingPdf && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={onOpenRegenerate}
                    disabled={!invoiceReadiness.isReady}
                  >
                    <RefreshCw className="mr-2 size-4" />
                    Regenerate PDF
                  </DropdownMenuItem>
                </>
              )}
              {isDraft && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onOpenMarkSent}>
                    <Check className="mr-2 size-4" />
                    Mark as sent
                  </DropdownMenuItem>
                </>
              )}
              {(isSent || isOverdue) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onOpenSend}>
                    <Send className="mr-2 size-4" />
                    Resend invoice
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onOpenMarkPaid}>
                    <Check className="mr-2 size-4" />
                    Mark as paid
                  </DropdownMenuItem>
                </>
              )}
              {isSent && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onTransition('OVERDUE')}
                    disabled={transitionIsPending}
                  >
                    <Flag className="mr-2 size-4" />
                    Mark as overdue
                  </DropdownMenuItem>
                </>
              )}
              {canCancel && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onTransition('CANCELLED')}
                    disabled={transitionIsPending}
                    className="text-destructive-text focus:text-destructive-text"
                  >
                    <XCircle className="mr-2 size-4" />
                    Cancel invoice
                  </DropdownMenuItem>
                </>
              )}
              {isArchived ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onUnarchive} disabled={unarchiveIsPending}>
                    <ArchiveRestore className="mr-2 size-4" />
                    Unarchive
                  </DropdownMenuItem>
                </>
              ) : (
                isArchivable && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onArchive} disabled={archiveIsPending}>
                      <Archive className="mr-2 size-4" />
                      Archive
                    </DropdownMenuItem>
                  </>
                )
              )}
              {isDraft && (
                <DropdownMenuItem
                  onClick={onOpenDelete}
                  className="text-destructive-text focus:text-destructive-text"
                >
                  <Ban className="mr-2 size-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" onClick={onOpenPreview}>
            <Eye className="mr-1.5 size-3.5" />
            Preview
          </Button>

          {canReissue && (
            <Button className="max-md:flex-1" onClick={onOpenReissue}>
              <RefreshCw className="mr-1.5 size-3.5" />
              Re-issue
            </Button>
          )}

          {isDraft &&
            (invoiceReadiness.isReady ? (
              <Button className="max-md:flex-1" onClick={onOpenSend}>
                <Send className="mr-1.5 size-3.5" />
                Send invoice
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="max-md:flex-1">
                    <Button disabled className="max-md:w-full">
                      <Send className="mr-1.5 size-3.5" />
                      Send invoice
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Complete your organization&apos;s business, tax, and payment details in
                  Settings before sending.
                </TooltipContent>
              </Tooltip>
            ))}
        </div>
      </div>
    </div>
  )
}
